import {
  Component,
  HostListener,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  AfterContentInit,
  Optional,
  Inject,
  forwardRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment';

import { TranslateService } from '@ngx-translate/core';

export enum TimepickerHourFormat {
  format12 = '12',
  format24 = '24',
}

export enum TimepickerSteps {
  hours = 1,
  minutes = 5,
}

/* tslint:disable */
export const CMX_TIMEPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CmxTimepickerComponent),
  multi: true,
};
/* tslint:enable */

interface StringMap {
  [s: string]: string;
}

@Component({
  providers: [CMX_TIMEPICKER_VALUE_ACCESSOR],
  selector: 'cmx-timepicker',
  templateUrl: './cmx-timepicker.component.html',
  styleUrls: ['./cmx-timepicker.component.scss'],
})
export class CmxTimepickerComponent implements AfterContentInit, ControlValueAccessor {
  @Input()
  hourFormat = TimepickerHourFormat.format12;
  @Input()
  placeHolder = '';
  @Input()
  minimum = moment();
  @Input()
  separator = ':';
  @Input()
  minuteStep = TimepickerSteps.minutes;
  @Input()
  disabled = false;
  @Input()
  propagateOnChange = true;
  @Input()
  writable = false;
  @Input()
  cycleCurrentDate = true;
  @Output()
  public change: EventEmitter<moment.Moment> = new EventEmitter<moment.Moment>();

  isDialogOpen = false;
  _time: moment.Moment;
  timeString = '';
  private _rtl = false;
  hours = '0';
  minutes = '0';
  meridians: StringMap = { am: 'AM', pm: 'PM' };
  meridian: string = this.meridians.am;
  isInitialised = false;

  lastCharOfFormatTime: string;
  public onChange: any = (_: any) => {};

  public onTouched: any = () => {};

  get rtl(): boolean {
    return this._rtl;
  }

  @Input()
  set rtl($value: boolean) {
    if ($value !== undefined && $value !== null) {
      this._rtl = $value;
    }
  }

  get time(): moment.Moment {
    return this._time;
  }

  @Input()
  set time($value: moment.Moment) {
    if ($value !== undefined && $value !== null) {
      this._time = $value;
      this.propagateTime();
    }
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: any) {
    if (!this._eref.nativeElement.contains(event.target)) {
      if (this.isDialogOpen) {
        this.toggleDialog();
      }
    }
  }

  /** Used to prevent triggering form submition action */
  @HostListener('document:keypress', ['$event'])
  enterKey(event: KeyboardEvent) {
    if (event.code === 'Enter') {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  constructor(
    @Optional()
    @Inject('RTL')
    isRTL: boolean,
    private _eref: ElementRef,
    private translateService: TranslateService,
    private ref: ChangeDetectorRef,
  ) {
    if (isRTL != undefined) {
      this.rtl = isRTL;
    }
    this.lastCharOfFormatTime = this.translateService.instant('global.formatTime').slice(-1);
  }

  ngAfterContentInit() {
    this.initTime();
  }

  public writeValue($value: moment.Moment): void {
    if ($value !== undefined && $value !== null) {
      if (typeof $value === 'string' || $value instanceof String) {
        const date = moment($value);
        this._time = date.isValid() ? date : moment(this.setCorrectStep(this.minimum));
      } else {
        this._time = $value;
      }

      if (!this.isTimeValid()) {
        this._time = moment(this.setCorrectStep(this.minimum));
      }
      this.propagateTime(true);
      if (!this.ref['destroyed']) {
        this.ref.detectChanges();
      }
    } else {
      this.timeString = '';
      if (!this.ref['destroyed']) {
        this.ref.detectChanges();
      }
    }
  }

  public registerOnChange($fn: any): void {
    this.onChange = $fn;
    this.onChange(this.time);
  }

  public registerOnTouched($fn: any): void {
    this.onTouched = $fn;
  }

  toggleDialog(): void {
    if (!this.time && !this.isInitialised) {
      this.setStartTime();
    }
    this.isDialogOpen = !this.isDialogOpen;

    if (!this.propagateOnChange && !this.isDialogOpen) {
      this.propagateTime(true);
    }
  }

  hourChange(diff: number): void {
    // Increase or decrease hours but don't affect day
    const newTime = this.cycleCurrentDate
      ? moment(this.time)
          .add(diff * TimepickerSteps.hours, 'hours')
          .day(this.time.day())
      : moment(this.time).add(diff * TimepickerSteps.hours, 'hours');

    const currentTime = moment();

    if (this.minimum && this.minimum.isBefore(currentTime)) {
      const minTime = moment.min(newTime, currentTime);
      this.time = minTime;
    } else {
      if (newTime.isAfter(currentTime)) {
        this.time = newTime;
      } else {
        this.time = moment(currentTime);
      }
    }

    this.setTime();
  }

  minuteChange(diff: number): void {
    // Increase or decrease minutes but don't affect day
    const newTime = this.cycleCurrentDate
      ? moment(this.time)
          .add(diff * this.minuteStep, 'minutes')
          .day(this.time.day())
      : moment(this.time).add(diff * this.minuteStep, 'minutes');

    const currentTime = moment();

    if (this.minimum && this.minimum.isBefore(currentTime)) {
      const minTime = moment.min(newTime, currentTime);
      this.time = minTime;
    } else {
      if (newTime.isAfter(currentTime)) {
        this.time = newTime;
      } else {
        this.time = moment(currentTime);
      }
    }

    this.setTime();
  }

  toggleMeridian(): void {
    if (this.time.hour() < 12) {
      this.time.add(12, 'h');
    } else {
      this.time.subtract(12, 'h');
    }

    this.setTime();
  }

  protected roundToStep(value: number, step = this.minuteStep) {
    return Math.ceil(value / step) * step;
  }

  private initTime(): void {
    if (this.time) {
      this.setTime();
    } else {
      if (!this.placeHolder && this.placeHolder === '') {
        this.setStartTime();
      }
    }
  }

  private setCorrectStep(date: moment.Moment): moment.Moment {
    return moment(date).minutes(0).add(this.roundToStep(date.minutes()), 'minutes');
  }

  private setStartTime(): void {
    this.time = moment();
    this.time.add(1, 'h');
    this.time.minute(0);
    this.time.second(0);

    this.isInitialised = true;
    this.setTime();
  }

  private isTimeValid(): boolean {
    if (
      this.minimum &&
      this.minimum.minutes() + this.minimum.hours() * 60 > this.time.minutes() + this.time.hours() * 60
    ) {
      return false;
    } else {
      return true;
    }
  }

  setTimeString(): void {
    let hours = this.time.hour();
    hours = this.hourFormat === '12' && hours >= 12 ? hours - 12 : hours;
    this.hours = (this.hourFormat === '12' && hours % 12 === 0 ? 12 : hours).toString();
    const minutes = this.time.minute();
    this.minutes = (minutes < 10 ? '0' : '') + minutes.toString();
    this.meridian = this.getMeridian(this.time.hour());
    this.timeString = `${this.hours}${this.separator}${this.minutes}`;

    if (this.hourFormat === '12') {
      this.timeString = this.timeString + ` ${this.meridian}`;
    }
  }
  /*
   * getMeridian: given an hour, returns the right meridian
   * @param  integer hour
   * @return string  meridian
   * */
  private getMeridian(hour) {
    const meridianChar =
      this.lastCharOfFormatTime === 'a' || this.lastCharOfFormatTime === 'A' ? this.lastCharOfFormatTime : 'A';
    this.meridians = meridianChar === 'a' ? { am: 'a.m', pm: 'p.m' } : { am: 'AM', pm: 'PM' };
    return hour >= 12 ? this.meridians.pm : this.meridians.am;
  }

  private setTime(): void {
    if (!this.isTimeValid()) {
      this.time = moment(this.setCorrectStep(this.minimum));
    }

    this.time = this.setCorrectStep(this.time);
    this.propagateTime();
  }

  private propagateTime(propagate = this.propagateOnChange) {
    this.setTimeString();
    if (propagate) {
      this.onChange(this.time);
      this.change.emit(this.time);
    }
  }

  /**
   * Triggers validation and propagation for input value changed by keyboard
   * @param type h = hours ; m = minutes
   * @param value value by keyboard, to validate and propagate
   */
  public keyboardChange(type: 'h' | 'm', target: EventTarget) {
    const value = Number((target as HTMLInputElement).value);
    if (type === 'h') {
      if (value >= 0 && value < 24) {
        const newTime = moment(this.time).hours(value).day(this.time.day());
        this.time = newTime;
        this.setTime();
      }
    } else {
      if (value >= 0 && value < 60) {
        const newTime = moment(this.time).minutes(value).day(this.time.day());
        this.time = newTime;
        this.setTime();
      }
    }
  }
}
